import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import adminDeptApi from "../../api/admin/AdminDept";
import {
  AddDeptParam,
  ChangeDeptNameParam,
  Dept,
  DeptDetail,
  DeptInfo,
  DeptManagerParam,
  ExternalUser,
  TreeNode,
  UpdateDeptParam,
  User,
  UserDetail,
  UserDTO,
  UserPasswd,
} from "../../../types/adminDept";
import dayjs from "dayjs";
import FileSaver from "file-saver";

const ROOT_DEPT_CODE = "ae4fc0f5fd8f4142834c1ad4e929a514";
// # initial state
const initialState = {
  currentDeptId: ROOT_DEPT_CODE,
  deptDetail: {} as DeptDetail,
  userDetail: {} as UserDetail,
  deptList: [] as Dept[],
  internalTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  internalDeptTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  internalActiveTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  internalSearchTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  externalTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  externalActiveTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  externalInActiveTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  externalLiteTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  externalSearchTree: {
    columnText: "",
    subTree: [],
    showChildren: false,
    hasChildren: false,
    value: "",
    type: "",
  } as TreeNode,
  searchUserResult: [] as UserDTO[],
};

export const trunkGetDeptLists = createAsyncThunk(
  "wapi/admins/depts",
  async () => {
    const resp = await adminDeptApi.getDeptLists();
    return resp;
  }
);

export const trunkGetDeptDetail = createAsyncThunk(
  "wapi/admins/depts/deptId/detail",
  async (deptId: string) => {
    const resp = await adminDeptApi.getDeptDetail(deptId);
    return { resp, deptId };
  }
);

export const trunkGetDeptUsers = createAsyncThunk(
  "wapi/admins/depts/deptId/users",
  async (deptId: string) => {
    const resp = await adminDeptApi.getDeptUsers(deptId);
    return { resp, deptId };
  }
);

export const trunkGetDeptInfo = createAsyncThunk(
  "wapi/admins/depts/info",
  async (deptId: string) => {
    const resp = await adminDeptApi.getDeptInfo(deptId);
    return resp;
  }
);

export const trunkGetUserDetail = createAsyncThunk(
  "wapi/admins/users/info",
  async (userId: string) => {
    const resp = await adminDeptApi.getUserDetail(userId);
    return resp;
  }
);

export const trunkUpdateUserDept = createAsyncThunk(
  "wapi/admins/users/update",
  async (userInfo: UserDTO) => {
    const resp = await adminDeptApi.updateUserDept(userInfo);
    return resp;
  }
);

export const trunkUpdateExternalUserDept = createAsyncThunk(
  "wapi/admins/users/update/external",
  async (userInfo: ExternalUser) => {
    const resp = await adminDeptApi.updateExternalUserDept(userInfo);
    return resp;
  }
);

export const trunkAddUserDept = createAsyncThunk(
  "wapi/admins/users/insert",
  async (userInfo: UserDTO) => {
    const resp = await adminDeptApi.addUserDept(userInfo);
    return resp;
  }
);

export const trunkGetActiveInternalUserList = createAsyncThunk(
  "wapi/admins/users/list/internal/active",
  async () => {
    const resp = await adminDeptApi.getActiveInternalUserList();
    return resp;
  }
);

export const trunkGetActiveExternalUserList = createAsyncThunk(
  "wapi/admins/users/list/external/active",
  async () => {
    const resp = await adminDeptApi.getActiveExternalUserList();
    return resp;
  }
);

export const trunkGetInActiveExternalUserList = createAsyncThunk(
  "wapi/admins/users/list/external/inactive",
  async () => {
    const resp = await adminDeptApi.getInActiveExternalUserList();
    return resp;
  }
);

export const trunkGetExternalLiteUserList = createAsyncThunk(
  "wapi/admins/users/list/external/lite",
  async () => {
    const resp = await adminDeptApi.getExternalLiteUserList();
    return resp;
  }
);

export const trunkGetInternalUserList = createAsyncThunk(
  "wapi/admins/users/list/internal",
  async (searchOpt: string) => {
    const resp = await adminDeptApi.getInternalUserList(searchOpt);
    return resp;
  }
);
export const trunkSearchUserList = createAsyncThunk(
  "wapi/admins/users/list/all",
  async (searchOpt: string) => {
    const resp = await adminDeptApi.searchUserList(searchOpt);
    return resp;
  }
);

export const trunkGetExternalUserList = createAsyncThunk(
  "wapi/admins/users/list/external",
  async (searchOpt: string) => {
    const resp = await adminDeptApi.getExternalUserList(searchOpt);
    return resp;
  }
);

export const trunkDownloadDeptFile = createAsyncThunk(
  "wapi/admins/users/list/excel",
  async () => {
    const resp = await adminDeptApi.downloadDeptFile();
    return resp;
  }
);

export const trunkUploadDeptFile = createAsyncThunk(
  "wapi/admins/users/list/excel/upload",
  async (files: any) => {
    const resp = await adminDeptApi.uploadDeptFile(files);
    return resp;
  }
);

export const trunkUpdateDeptParent = createAsyncThunk(
  "wapi/admins/depts/update",
  async ({ code, parentCode }: UpdateDeptParam) => {
    const resp = await adminDeptApi.updateDeptParent(code, parentCode);
    return resp;
  }
);

export const trunkUpdateDeptManager = createAsyncThunk(
  "wapi/admins/depts/update/manager",
  async (param: DeptManagerParam) => {
    const resp = await adminDeptApi.updateDeptManager(param);
    return resp;
  }
);

export const trunkDeleteDept = createAsyncThunk(
  "wapi/admins/depts/delete/dept",
  async (code: string) => {
    const resp = await adminDeptApi.deleteDept(code);
    return resp;
  }
);

export const trunkDeleteUser = createAsyncThunk(
  "wapi/admins/depts/delete/user",
  async (list: { id: string }[]) => {
    const resp = await adminDeptApi.deleteUser(list);
    return resp;
  }
);

export const trunkAddDept = createAsyncThunk(
  "wapi/admins/depts/insert",
  async (param: AddDeptParam) => {
    const resp = await adminDeptApi.addDept(param);
    return resp;
  }
);

export const trunkUserPasswdUpdate = createAsyncThunk(
  "wapi/admins/users/passwd/update",
  async (param: UserPasswd) => {
    const resp = await adminDeptApi.userPasswdUpdate(param);
    return resp;
  }
);

export const trunkUserPasswdInit = createAsyncThunk(
  "papi/passwd/init/admin",
  async (param: string) => {
    const resp = await adminDeptApi.userPasswdInit(param);
    return resp;
  }
);

export const trunkChangeDeptName = createAsyncThunk(
  "wapi/admins/depts/update/name",
  async (param: ChangeDeptNameParam) => {
    const resp = await adminDeptApi.changeDeptName(param);
    return resp;
  }
);

const removeNodes = (
  root: TreeNode,
  shouldRemove: (node: TreeNode) => boolean
) => {
  const dfs = (node: TreeNode) => {
    if (!node?.subTree) return;

    node.subTree = node.subTree.filter((child) => !shouldRemove(child));

    node.subTree.forEach((child) => dfs(child));

    // 삭제 후에 비어버린 dept 노드는 접히도록(UX)
    if (node.type === "dept") {
      if (node.subTree.length === 0) {
        node.hasChildren = false;
        node.showChildren = false;
      }
    }
  };

  dfs(root);
};
const mergeDeptNodesKeepOpenState = (
  oldDepts: TreeNode[],
  newDepts: TreeNode[]
) => {
  const map = new Map(oldDepts.map((n) => [n.value, n]));

  return newDepts.map((n) => {
    const old = map.get(n.value);
    if (!old) return n;

    const hasChildren = !!n.hasChildren;

    return {
      ...old, // ✅ 기존 subTree/showChildren 유지
      columnText: n.columnText,
      type: n.type,
      hasChildren,
      // ✅ 더 이상 자식 없으면 펼침/자식 제거(안전)
      showChildren: hasChildren ? old.showChildren : false,
      subTree: hasChildren ? old.subTree : [],
    };
  });
};
const adminDeptSlice = createSlice({
  name: "adminDept",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(trunkGetDeptLists.fulfilled, (state, action) => {
      state.deptList = action.payload;
    });
    builder.addCase(
      trunkGetDeptDetail.fulfilled,
      (state, { payload: deptDetail }) => {
        if (deptDetail.deptId !== state.currentDeptId) return;

        state.deptDetail.depts = deptDetail.resp;
        state.deptDetail.deptCode = deptDetail.deptId;
        if (deptDetail.deptId === ROOT_DEPT_CODE) {
          const oldDepts = state.internalTree.subTree.filter(
            (n) => n.type === "dept"
          );
          const oldUsers = state.internalTree.subTree.filter(
            (n) => n.type === "user"
          );

          const newDepts: TreeNode[] = [];
          deptDetail.resp.forEach((param) => {
            newDepts.push({
              columnText: param?.name ?? (param as any)?.userName,
              subTree: [],
              showChildren: param.deptCount > 0 || param.userCount > 0,
              hasChildren: param.deptCount > 0 || param.userCount > 0,
              value: param.code,
              type: "dept",
            });
          });

          const mergedDepts = mergeDeptNodesKeepOpenState(oldDepts, newDepts);
          // dept 먼저, user 뒤(기존 구조 유지)
          state.internalTree.subTree = [...mergedDepts, ...oldUsers];
        } else {
          state.internalTree.subTree.forEach((param) => {
            makeDeptSubNodes(deptDetail.resp, "dept", deptDetail.deptId, param);
          });
        }
      }
    );
    builder.addCase(trunkDeleteUser.fulfilled, (state, action) => {
      // action.meta.arg = [{id: "..."}]
      const ids = new Set((action.meta.arg ?? []).map((x) => x.id));

      const shouldRemoveUser = (node: TreeNode) =>
        ids.has(node.value) && node.type !== "dept" && node.type !== "company";

      removeNodes(state.internalTree, shouldRemoveUser);
      removeNodes(state.internalDeptTree, shouldRemoveUser);
      removeNodes(state.internalActiveTree, shouldRemoveUser);
      removeNodes(state.internalSearchTree, shouldRemoveUser);

      removeNodes(state.externalTree, shouldRemoveUser);
      removeNodes(state.externalActiveTree, shouldRemoveUser);
      removeNodes(state.externalInActiveTree, shouldRemoveUser);
      removeNodes(state.externalLiteTree, shouldRemoveUser);
      removeNodes(state.externalSearchTree, shouldRemoveUser);
    });
    builder.addCase(
      trunkGetDeptUsers.fulfilled,
      (state, { payload: userDetail }) => {
        if (userDetail.deptId !== state.currentDeptId) return;

        state.userDetail.users = userDetail.resp;
        state.userDetail.deptCode = userDetail.deptId;
        if (userDetail.deptId === ROOT_DEPT_CODE) {
          const oldDepts = state.internalTree.subTree.filter(
            (n) => n.type === "dept"
          );
          const newUsers: TreeNode[] = [];
          userDetail.resp.forEach((param) => {
            newUsers.push({
              columnText: param?.name ?? (param as any)?.userName,
              subTree: [],
              showChildren: false,
              hasChildren: false,
              value: param?.id ?? (param as any)?.userId,
              type: "user",
            });
          });
          state.internalTree.subTree = [...oldDepts, ...newUsers];
          //  state.internalTree.setUser = true;
        } else {
          state.internalTree.subTree.forEach((param) => {
            makeUserSubNodes(userDetail.resp, "dept", userDetail.deptId, param);
          });
        }
      }
    );
    builder.addCase(trunkGetExternalUserList.fulfilled, (state, action) => {
      if (action.meta.arg !== "") {
        state.externalSearchTree.subTree = [];
        action.payload.forEach((param) => {
          state.externalSearchTree.subTree.push({
            columnText: param.userType === 3 ? "" : param.userName ?? "",
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: param.userId,
            type: "externalUser",
          });
        });
      } else {
        state.externalTree.subTree = [];
        action.payload.forEach((param) => {
          state.externalTree.subTree.push({
            columnText: param.userType === 3 ? "" : param.userName ?? "",
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: param.userId,
            type: "externalUser",
          });
        });
      }
    });
    builder.addCase(
      trunkSearchUserList.fulfilled,
      (state, { payload: userList }) => {
        state.searchUserResult = userList.users;
      }
    );
    builder.addCase(
      trunkGetInternalUserList.fulfilled,
      (state, { payload: userList }) => {
        state.internalSearchTree.subTree = [];
        (userList.users ?? userList).forEach((param) => {
          state.internalSearchTree.subTree.push({
            columnText: param?.name ?? (param as any)?.userName,
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: param?.id ?? (param as any)?.userId,
            type: "user",
          });
        });
      }
    );
    builder.addCase(
      trunkGetActiveInternalUserList.fulfilled,
      (state, { payload: userList }) => {
        state.internalActiveTree.subTree = [];
        userList.forEach((param) => {
          state.internalActiveTree.subTree.push({
            columnText: param.userName,
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: param.userId,
            type: "user",
          });
        });
      }
    );
    builder.addCase(
      trunkGetActiveExternalUserList.fulfilled,
      (state, { payload: userList }) => {
        state.externalActiveTree.subTree = [];
        userList.forEach((param) => {
          state.externalActiveTree.subTree.push({
            columnText: param.userType === 3 ? "" : param.userName ?? "",
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: param.userId,
            type: "externalUser",
          });
        });
      }
    );
    builder.addCase(
      trunkGetInActiveExternalUserList.fulfilled,
      (state, { payload: userList }) => {
        state.externalInActiveTree.subTree = [];
        userList.forEach((param) => {
          state.externalInActiveTree.subTree.push({
            columnText: param.userType === 3 ? "" : param.userName ?? "",
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: param.userId,
            type: "externalUser",
          });
        });
      }
    );
    builder.addCase(
      trunkGetExternalLiteUserList.fulfilled,
      (state, { payload: userList }) => {
        state.externalLiteTree.subTree = [];
        userList.forEach((param) => {
          state.externalLiteTree.subTree.push({
            columnText: param.userType === 3 ? "" : param.userName ?? "",
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: param.userId,
            type: "externalUser",
          });
        });
      }
    );
    builder.addCase(
      trunkChangeDeptName.fulfilled,
      (state, { payload: result }) => {
        //조직도(트리) 쪽 부서명 변경
        state.internalTree.subTree.forEach((param) => {
          if (param.value === result.code) {
            param.columnText = result.name;
          } else {
            updateTreeItem(
              param.subTree,
              "",
              result.code,
              result.name,
              state.internalTree,
              "dept"
            );
          }
        });
        state.userDetail.users.forEach((item) => {
          if (item.dept.code === result.code) {
            item.dept.name = result.name;
          }
        });
      }
    );
    builder.addCase(trunkGetDeptDetail.pending, (state, action) => {
      state.currentDeptId = action.meta.arg; // deptId
    });
    builder.addCase(trunkGetDeptUsers.pending, (state, action) => {
      state.currentDeptId = action.meta.arg; // deptId
    });
    builder.addCase(trunkAddDept.fulfilled, (state, { payload: result }) => {
      const parentCode = result.parentCode;
      const code = result.code;

      //부서 삽입(하위부서 테이블)
      state.deptDetail.depts.push({
        code: result.code,
        name: result.name,
        managerId: "",
        managerName: "",
        deptCount: 0,
        userCount: 0,
      });

      // 정렬
      state.deptDetail.depts.sort(function (a: DeptInfo, b: DeptInfo) {
        const upperCaseA = a.name.toUpperCase();
        const upperCaseB = b.name.toUpperCase();

        if (upperCaseA > upperCaseB) return 1;
        if (upperCaseA < upperCaseB) return -1;
        if (upperCaseA === upperCaseB) return 0;
        return 0;
      });

      //부서 삽입(트리)
      if (ROOT_DEPT_CODE === parentCode) {
        state.internalTree.subTree.push({
          columnText: result?.name ?? (result as any).userName,
          subTree: [],
          showChildren: false,
          hasChildren: false,
          value: code,
          type: "dept",
        });
        state.internalTree.subTree.sort(function (a: TreeNode, b: TreeNode) {
          return sortFunction(
            a.columnText + "",
            b.columnText + "",
            a.type,
            b.type
          );
        });
      } else {
        state.internalTree.subTree.forEach((param, index) => {
          if (param.value === parentCode) {
            param.subTree.push({
              columnText: result?.name ?? (result as any).userName,
              subTree: [],
              showChildren: false,
              hasChildren: false,
              value: code,
              type: "dept",
            });
            param.hasChildren = true;

            param.subTree.sort(function (a: TreeNode, b: TreeNode) {
              return sortFunction(
                a.columnText + "",
                b.columnText + "",
                a.type,
                b.type
              );
            });
          } else if (param.subTree.length > 0 && parentCode) {
            updateTreeItem(
              param.subTree,
              parentCode,
              code,
              result.name,
              state.internalTree,
              "dept"
            );
          }
        });
      }
    });
    builder.addCase(
      trunkUpdateUserDept.fulfilled,
      (state, { payload: result }) => {
        const user = result[0];
        const userId = user.id;
        const userName = user.name;
        const newDeptCode = user.depts.primary.code; // API 결과에 새 부서 코드가 포함되어 있어야 합니다.
        state.internalSearchTree.subTree = state.internalSearchTree.subTree.map(
          (item) =>
            item.value === userId ? { ...item, columnText: userName } : item
        );
        state.internalActiveTree.subTree = state.internalActiveTree.subTree.map(
          (item) =>
            item.value === userId ? { ...item, columnText: userName } : item
        );
        const shouldRemoveUser = (node: TreeNode) =>
          node.value === userId && node.type === "user";

        removeNodes(state.internalTree, shouldRemoveUser);

        // state.internalTree.subTree = updateNodeTextByValue(
        //   state.internalTree.subTree,
        //   result[0].id,
        //   result[0].name
        // );
        if (newDeptCode === ROOT_DEPT_CODE) {
          state.internalTree.subTree.push({
            columnText: userName,
            subTree: [],
            showChildren: false,
            hasChildren: false,
            value: userId,
            type: "user",
          });
        } else if (newDeptCode) {
          // 기존에 정의된 updateTreeItem을 사용하여 새 부모(부서) 아래에 삽입합니다.
          updateTreeItem(
            state.internalTree.subTree,
            newDeptCode, // parentCode
            userId, // code
            userName, // name
            state.internalTree,
            "user" // type
          );
        }
      }
    );
    builder.addCase(
      trunkUpdateExternalUserDept.fulfilled,
      (state, { payload: result }) => {
        state.externalSearchTree.subTree = state.externalSearchTree.subTree.map(
          (item) =>
            item.value === result.userId
              ? { ...item, columnText: result.userName ?? "" }
              : item
        );
        state.externalTree.subTree = updateNodeTextByValue(
          state.externalTree.subTree,
          result.userId,
          result.userName ?? ""
        );
      }
    );
    builder.addCase(trunkUpdateDeptParent.fulfilled, (state, action) => {
      const dept = action.payload;
      const code = dept.code;
      const parentCode = dept.parentCode;
      let showChildren = false;
      let hasChildren = false;
      //이동 대상 부서 삭제
      state.internalTree.subTree.forEach((param, index) => {
        if (param.value === code) {
          showChildren = param.showChildren;
          hasChildren = param.hasChildren;
          state.internalTree.subTree.splice(index, 1);
        } else if (param.subTree.length > 0) {
          hasChildren = removeTreeItem(
            param,
            code,
            state.internalTree,
            "remove"
          );
        }
      });
      //이동 대상 부서 삽입
      // ROOT 체크
      if (parentCode === ROOT_DEPT_CODE) {
        state.internalTree.subTree.push({
          columnText: dept.name,
          subTree: [],
          showChildren: showChildren,
          hasChildren: hasChildren,
          value: code,
          type: "dept",
        });
        state.internalTree.subTree.sort(function (a: TreeNode, b: TreeNode) {
          return sortFunction(a.columnText, b.columnText, a.type, b.type);
        });
      } else {
        state.internalTree.subTree.forEach((param, index) => {
          if (param.value === parentCode) {
            param.subTree.push({
              columnText: dept.name,
              subTree: [],
              showChildren: showChildren,
              hasChildren: hasChildren,
              value: code,
              type: "dept",
            });

            param.subTree.sort(function (a: TreeNode, b: TreeNode) {
              return sortFunction(a.columnText, b.columnText, a.type, b.type);
            });
          } else if (param.subTree.length > 0 && parentCode) {
            updateTreeItem(
              param.subTree,
              parentCode,
              code,
              dept.name,
              state.internalTree,
              "dept",
              showChildren,
              hasChildren
            );
          }
        });
      }
    });
    builder.addCase(
      trunkDownloadDeptFile.fulfilled,
      (state, { payload: res }) => {
        if (res.data && res.headers) {
          const file = res.data;
          const fileName = dayjs().format("YYYYMMDD") + "_users.xlsx";
          FileSaver.saveAs(file, fileName);
        }
      }
    );
  },
});

const makeDeptSubNodes = (
  items: DeptInfo[],
  clickType: string,
  clickValue: string,
  treeNode: TreeNode
): void => {
  if (treeNode.type === clickType && treeNode.value === clickValue) {
    let temps: TreeNode[] = [];
    items.forEach((param, idx) => {
      temps.unshift({
        columnText: param?.name ?? (param as any)?.userName,
        subTree: [],
        showChildren: param.deptCount > 0 || param.userCount > 0 ? true : false,
        hasChildren: param.deptCount > 0 || param.userCount > 0 ? true : false,
        value: param.code,
        type: "dept",
      });
    });
    //  treeNode.setDept = true;
    treeNode.showChildren = true;
    // treeNode.hasChildren = true;

    treeNode.subTree = treeNode.subTree.filter(function (data) {
      return data.type === "user";
    });

    temps.forEach((param, idx) => {
      treeNode.subTree.unshift(param);
    });
  } else {
    treeNode.subTree.forEach((param) => {
      makeDeptSubNodes(items, clickType, clickValue, param);
    });
  }
};

const makeUserSubNodes = (
  items: User[],
  clickType: string,
  clickValue: string,
  treeNode: TreeNode
): void => {
  if (treeNode.type === clickType && treeNode.value === clickValue) {
    treeNode.subTree = treeNode.subTree.filter(function (data) {
      return data.type === "dept";
    });

    items.forEach((param) => {
      treeNode.subTree.push({
        columnText: param?.name ?? (param as any)?.userName,
        subTree: [],
        showChildren: false,
        hasChildren: false,
        value: param.id,
        type: "user",
      });
    });
    // treeNode.setUser = true;
    treeNode.showChildren = true;
    //  treeNode.hasChildren = true;
  } else {
    treeNode.subTree.forEach((param) => {
      makeUserSubNodes(items, clickType, clickValue, param);
    });
  }
};

const updateTreeItem = (
  tree: TreeNode[],
  parentCode: string,
  code: string,
  name: string,
  state: TreeNode,
  type: string,
  showChildren?: boolean,
  hasChildren?: boolean
) => {
  tree.forEach((param, index) => {
    if (type === "dept" && param.value === parentCode) {
      //부서 이동일때
      param.subTree.push({
        columnText: name,
        subTree: [],
        showChildren: showChildren ? showChildren : false,
        hasChildren: hasChildren ? hasChildren : false,
        value: code,
        type: "dept",
      });
      param.hasChildren = true;

      param.subTree.sort(function (a: TreeNode, b: TreeNode) {
        return sortFunction(
          a.columnText + "",
          b.columnText + "",
          a.type,
          b.type
        );
      });
    } else if (type === "user" && param.value === parentCode) {
      //사용자 이동일때
      param.subTree.push({
        columnText: name,
        subTree: [],
        showChildren: false,
        hasChildren: false,
        value: code,
        type: "user",
      });
      param.hasChildren = true;

      param.subTree.sort(function (a: TreeNode, b: TreeNode) {
        return sortFunction(
          a.columnText + "",
          b.columnText + "",
          a.type,
          b.type
        );
      });
    } else if (
      (type === "dept" || type === "user") &&
      parentCode === "" &&
      param.subTree.length === 0
    ) {
      //부서명만 업데이트 시
      tree.forEach((item) => {
        if (item.value === code) {
          item.columnText = name;
        }
      });
    } else if (param.subTree.length > 0) {
      updateTreeItem(
        param.subTree,
        parentCode,
        code,
        name,
        state,
        type,
        showChildren,
        hasChildren
      );
    }
  });
};

const removeTreeItem = (
  subTree: TreeNode,
  deptCode: string,
  deptTree: TreeNode,
  status: string
): boolean => {
  let hasChildren = false;
  subTree.subTree.forEach((param, index) => {
    let isFind = false;
    if (param.value === deptCode) {
      hasChildren = param.hasChildren;
      subTree.subTree.splice(index, 1);
      if (status === "remove") {
        isFind = true;
      }
    } else if (param.subTree.length > 0) {
      return removeTreeItem(param, deptCode, deptTree, status);
    }

    if (isFind && subTree.subTree.length === 0) {
      subTree.hasChildren = false;
    }
  });

  return hasChildren;
};

const sortFunction = (
  a_columnText: string,
  b_columnText: string,
  a_type: string,
  b_type: string
): number => {
  const upperCaseA = a_columnText.toUpperCase();
  const upperCaseB = b_columnText.toUpperCase();
  if (a_type === "dept" && b_type === "user") return -1;
  if (a_type === "user" && b_type === "dept") return 1;
  if (upperCaseA > upperCaseB) return 1;
  if (upperCaseA < upperCaseB) return -1;
  if (upperCaseA === upperCaseB) return 0;
  return 0;
};
function updateNodeTextByValue(
  nodes: TreeNode[],
  targetValue: string,
  newText: string
): TreeNode[] {
  return nodes.map((node) => {
    const updatedSubTree =
      node.subTree && node.subTree.length > 0
        ? updateNodeTextByValue(node.subTree, targetValue, newText)
        : node.subTree;

    if (node.value === targetValue) {
      return { ...node, columnText: newText, subTree: updatedSubTree };
    }

    // 자식이 바뀌었을 수도 있으니 반영
    if (updatedSubTree !== node.subTree) {
      return { ...node, subTree: updatedSubTree };
    }

    return node;
  });
}

export const adminDeptActions = adminDeptSlice.actions;
export default adminDeptSlice.reducer;
